import type { LayersList } from "@deck.gl/core";
import type { GeoJSON } from "geojson";

import type { ComparisonArea, Location, PinnedLocation, ResolutionType } from "~/lib/map/types";
import {
  initAreaViewLayer,
  initComparisonAreaViewLayers,
  initHexagonSelectLayer,
  initLocationsLayer,
  initPinnedLocationsLayer,
} from "~/lib/map/utils";

import { useMapBase } from "./useMapBase";

type useEditMapProps = {
  width: number;
  height: number;
  resolution?: ResolutionType;
  areaH3Indexes: string[];
  geometry: GeoJSON | null;
  comparisonAreas?: ComparisonArea[];
  pinnedLocations?: PinnedLocation[];
  searchedLocation?: Location | null;
  inViewportLocations?: Location[];
  enableHexagonSelection?: boolean;
  onHexagonClick: (h3Index: string) => void;
  onLocationClick: (location: Location) => void;
};

export const useEditMap = ({
  width,
  height,
  resolution = 3,
  areaH3Indexes,
  geometry,
  comparisonAreas,
  pinnedLocations,
  searchedLocation,
  inViewportLocations,
  enableHexagonSelection = false,
  onHexagonClick = () => {},
  onLocationClick = () => {},
}: useEditMapProps) => {
  const { viewState, onViewStateChange, setViewState } = useMapBase({
    width,
    height,
    areaH3Indexes,
    geometry,
  });

  const layers: LayersList = [initAreaViewLayer(areaH3Indexes, geometry)];

  if (enableHexagonSelection) {
    layers.push(initHexagonSelectLayer(viewState, resolution, onHexagonClick));
  }

  if (comparisonAreas) {
    layers.push(...initComparisonAreaViewLayers(comparisonAreas));
  }

  if (pinnedLocations) {
    layers.push(initPinnedLocationsLayer(pinnedLocations));
  }

  // Copy, don't alias: `inViewportLocations` is parent state — pushing into it directly
  // mutates that array on every render, growing it unbounded (a memory leak).
  const locations = [...(inViewportLocations ?? [])];
  if (searchedLocation) {
    locations.push(searchedLocation);
  }
  if (locations.length) {
    // Filter out duplicate locations
    let filteredLocations = Array.from(new Map(locations.map((location) => [location.geoItemId, location])).values());
    if (pinnedLocations) {
      filteredLocations = filteredLocations.filter(
        (location) => !pinnedLocations.some((pinnedLocation) => pinnedLocation.geoItemId === location.geoItemId),
      );
    }
    layers.push(initLocationsLayer(filteredLocations, onLocationClick));
  }

  return { layers, viewState, onViewStateChange, setViewState };
};
